import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantAPI, Tenant } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Icon from '../components/Icon';
import Empty from '../components/ui/Empty';
import { fmt } from '../utils/format';

/**
 * Super Admin: Tenant management.
 * Lists all tenants and allows create / edit / delete.
 * Per-tenant SES/NTH credentials live on a separate page (/admin/tenants/:id/config).
 */

const AV_BG = ['var(--brand-500)', 'var(--ch-sms)', 'var(--ch-viber)', 'var(--ch-email)'];
const avColor = (key: string) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AV_BG[h % AV_BG.length];
};
/** Usage numbers pulled from the per-tenant stats endpoint. */
interface TenantUsage {
  contacts: number;
  emails: number;
  users: number;
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

/** Usage bar: shown value over its limit, turns red past 85%. */
const UsageBar: React.FC<{ value: number; max: number; color?: string }> = ({
  value,
  max,
  color = 'var(--brand-500)',
}) => {
  const p = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const over = p > 85;
  return (
    <div style={{ minWidth: 160 }}>
      <div className="flex items-center justify-between" style={{ fontSize: 12, marginBottom: 5 }}>
        <span className="tnum muted">
          {fmt(value)} / {fmt(max)}
        </span>
        <span className="tnum" style={{ fontWeight: 700, color: over ? 'var(--danger)' : 'var(--text-2)' }}>
          {p}%
        </span>
      </div>
      <div className="progress thin">
        <span style={{ width: p + '%', background: over ? 'var(--danger)' : color }} />
      </div>
    </div>
  );
};

const TenantList: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const tr = useT();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<Record<number, TenantUsage>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    domain: '',
    subscriptionTier: 'basic' as 'basic' | 'pro' | 'enterprise',
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tenantAPI.getAll();
      if (res.data.success) {
        const list = res.data.data.tenants;
        setTenants(list);
        loadStats(list);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Učitavanje klijenata nije uspelo', 'Failed to load tenants'));
    } finally {
      setLoading(false);
    }
  };

  // Enrich each tenant row with current-month usage (additive, non-blocking).
  const loadStats = async (list: Tenant[]) => {
    const results = await Promise.all(
      list.map(async (t) => {
        try {
          const res = await tenantAPI.getStats(t.id);
          const d = res.data?.data;
          if (res.data?.success && d) {
            return [
              t.id,
              {
                contacts: d.contacts?.current ?? 0,
                emails: d.messages?.email ?? 0,
                users: d.users?.current ?? 0,
              } as TenantUsage,
            ] as const;
          }
        } catch {
          /* ignore per-tenant stat failures */
        }
        return null;
      })
    );
    const map: Record<number, TenantUsage> = {};
    for (const r of results) if (r) map[r[0]] = r[1];
    setStats(map);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const res = await tenantAPI.create({
        name: newTenant.name,
        slug: newTenant.slug,
        domain: newTenant.domain || undefined,
        subscriptionTier: newTenant.subscriptionTier,
      });
      if (res.data.success) {
        setSuccess(tr(`Klijent „${res.data.data.tenant.name}" je kreiran.`, `Tenant "${res.data.data.tenant.name}" created.`));
        setNewTenant({ name: '', slug: '', domain: '', subscriptionTier: 'basic' });
        setShowCreate(false);
        loadTenants();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Kreiranje klijenta nije uspelo', 'Failed to create tenant'));
    }
  };

  const handleToggleActive = async (tenant: Tenant) => {
    setError('');
    try {
      await tenantAPI.update(tenant.id, { isActive: !tenant.isActive });
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Ažuriranje klijenta nije uspelo', 'Failed to update tenant'));
    }
  };

  const handleDelete = async (tenant: Tenant) => {
    const confirmText = window.prompt(
      tr(
        `Ovo će trajno obrisati klijenta „${tenant.name}" i SVE njegove podatke.\n\nUkucajte DELETE za potvrdu:`,
        `This will permanently delete tenant "${tenant.name}" and ALL its data.\n\nType DELETE to confirm:`
      )
    );
    if (confirmText !== 'DELETE') {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await tenantAPI.remove(tenant.id);
      setSuccess(tr(`Klijent „${tenant.name}" je obrisan.`, `Tenant "${tenant.name}" deleted.`));
      loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.error || tr('Brisanje klijenta nije uspelo', 'Failed to delete tenant'));
    }
  };

  // Auto-suggest slug from name
  const onNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setNewTenant((prev) => ({ ...prev, name, slug: prev.slug || slug }));
  };

  if (role !== 'super_admin') {
    return (
      <div className="page page-wide">
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)' }}>
          <strong>{tr('Pristup odbijen.', 'Access denied.')}</strong> {tr('Ova stranica je dostupna samo super administratorima.', 'This page is only available to super administrators.')}
        </div>
      </div>
    );
  }

  const tierBadge = (tier: string) => {
    const m: Record<string, [string, string]> = {
      basic: ['badge-draft', 'Basic'],
      pro: ['badge-sending', 'Pro'],
      enterprise: ['badge-completed', 'Enterprise'],
    };
    const [cls, label] = m[tier] || ['badge-draft', tier];
    return <span className={`badge ${cls}`}>{label}</span>;
  };

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <div className="page-title">{tr('Klijenti', 'Tenants')}</div>
          <div className="page-sub">{tr('Upravljanje klijentima i njihovim pretplatama.', 'Manage tenants and their subscriptions.')}</div>
        </div>
        <div className="flex items-center gap-12">
          <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
            <Icon name="users" size={16} /> {tr('Korisnici', 'Users')}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Icon name="plus" size={16} /> {tr('Nova kompanija', 'New company')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--brand-500)', color: 'var(--brand-700)', marginBottom: 16 }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="card-pad muted">{tr('Učitavanje…', 'Loading…')}</div>
        ) : tenants.length === 0 ? (
          <Empty
            icon="building"
            title={tr('Nema klijenata', 'No tenants')}
            sub={tr('Kreiraj prvu kompaniju da biste počeli.', 'Create your first company to get started.')}
            action={
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={16} /> {tr('Nova kompanija', 'New company')}
              </button>
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>{tr('Klijent', 'Tenant')}</th>
                <th>{tr('Pretplata', 'Tier')}</th>
                <th>{tr('Aktivan', 'Active')}</th>
                <th style={{ width: 220 }}>{tr('Kontakti', 'Contacts')}</th>
                <th style={{ width: 220 }}>{tr('Email (mesec)', 'Email (month)')}</th>
                <th>{tr('Korisnici', 'Users')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td style={{ paddingLeft: 22 }}>
                    <div className="flex items-center gap-12">
                      <span className="av-mini" style={{ background: avColor(t.slug) }}>
                        {initials(t.name)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{t.name}</div>
                        <div className="dim mono" style={{ fontSize: 12 }}>
                          {t.domain || `${t.slug}.vemid.rs`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{tierBadge(t.subscriptionTier)}</td>
                  <td>
                    <button
                      className={`switch ${t.isActive ? 'on' : ''}`}
                      aria-label={t.isActive ? tr('Deaktiviraj', 'Deactivate') : tr('Aktiviraj', 'Activate')}
                      title={t.isActive ? tr('Aktivan', 'Active') : tr('Neaktivan', 'Inactive')}
                      onClick={() => handleToggleActive(t)}
                    />
                  </td>
                  <td>
                    <UsageBar value={stats[t.id]?.contacts ?? 0} max={t.maxContacts} />
                  </td>
                  <td>
                    <UsageBar value={stats[t.id]?.emails ?? 0} max={t.maxEmailsPerMonth} color="var(--ch-sms)" />
                  </td>
                  <td className="tnum" style={{ fontWeight: 600 }}>
                    {fmt(stats[t.id]?.users ?? 0)} / {fmt(t.maxUsers)}
                  </td>
                  <td style={{ textAlign: 'right', paddingRight: 18, whiteSpace: 'nowrap' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      title={tr('Kredencijali / Konfiguracija', 'Credentials / Configuration')}
                      onClick={() => navigate(`/admin/tenants/${t.id}/config`)}
                    >
                      <Icon name="settings" size={15} /> {tr('Konfiguracija', 'Config')}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      title={tr('Obriši', 'Delete')}
                      onClick={() => handleDelete(t)}
                      style={{ marginLeft: 4, color: 'var(--danger)' }}
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <div className="overlay" onClick={() => setShowCreate(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={handleCreate}>
            <div className="modal-head">
              <div className="card-title">{tr('Nova kompanija', 'New company')}</div>
              <button type="button" className="icon-btn" aria-label={tr('Zatvori', 'Close')} onClick={() => setShowCreate(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <div className="field">
                  <label className="label">{tr('Naziv *', 'Name *')}</label>
                  <input
                    className="input"
                    type="text"
                    required
                    value={newTenant.name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Acme d.o.o."
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Slug * (a-z, 0-9, -)', 'Slug * (a-z, 0-9, -)')}</label>
                  <input
                    className="input"
                    type="text"
                    required
                    value={newTenant.slug}
                    onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
                    placeholder="acme"
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Domen (opciono)', 'Domain (optional)')}</label>
                  <input
                    className="input"
                    type="text"
                    value={newTenant.domain}
                    onChange={(e) => setNewTenant({ ...newTenant, domain: e.target.value })}
                    placeholder="acme.com"
                  />
                </div>
                <div className="field">
                  <label className="label">{tr('Pretplata', 'Subscription')}</label>
                  <select
                    className="select"
                    value={newTenant.subscriptionTier}
                    onChange={(e) => setNewTenant({ ...newTenant, subscriptionTier: e.target.value as any })}
                  >
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                {tr('Otkaži', 'Cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {tr('Kreiraj', 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TenantList;
